import {
  Accordion,
  Badge,
  Divider,
  Group,
  Paper,
  ScrollArea,
  Stack,
  Text,
} from '@mantine/core';
import { FC } from 'react';

import {
  MapActionType,
  PreviewPolygonResponse,
  RecentMapAction,
  SamplingPeriod,
  SmsBatchProgress,
  SmsFarmerProgress,
} from '../../data-access/types/gp-sampling-assignment';

import { actionHistoryDate, actionTypeLabelKey, smsDispatchIdFromAction } from './action-history';
import { AssignmentMapActionPanel } from './assignment-map-action-panel';
import { AssignmentMapFilterBar } from './assignment-map-filter-bar';
import {
  AssignmentFilterOptions,
  AssignmentMapFilters,
} from './assignment-map-filters';
import type { DrawActionMode } from './assignment-map-constants';
import type { AssignmentMapCapabilities } from '../../lib/assignment-context';

function formatSmsStamp(value: string | null): string | null {
  if (!value) {
    return null;
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  return date.toLocaleString();
}

const FarmerSmsRow: FC<{
  farmer: SmsFarmerProgress;
  i18n: (key: string, options?: Record<string, unknown>) => string;
}> = ({ farmer, i18n }) => {
  const failed = farmer.validation_form_status === 'ERROR';
  const sending =
    farmer.validation_form_status == null ||
    farmer.validation_form_status === 'INCOMPLETE';
  const lastReminder = formatSmsStamp(
    farmer.reminder3_sent_at ?? farmer.reminder2_sent_at ?? farmer.reminder1_sent_at,
  );
  const phone = farmer.phone_number ?? i18n('smsPhoneMissing');
  const errorLabel =
    farmer.sms_error === 'missing_phone'
      ? i18n('smsErrorMissingPhone')
      : farmer.sms_error === 'provider_error'
        ? i18n('smsErrorProvider')
        : null;

  return (
    <Stack gap={0}>
      <Group gap="xs" justify="space-between" wrap="nowrap">
        <Text lineClamp={1} size="xs" style={{ flex: 1 }}>
          {farmer.farm_name}
        </Text>
        <Text c={failed ? 'red' : 'dimmed'} size="xs">
          {failed
            ? i18n('smsStatusError')
            : sending
              ? i18n('smsStatusSending')
              : `${farmer.fields_validated}/${farmer.fields_total}`}
        </Text>
      </Group>
      <Text c="dimmed" size="xs">
        {phone}
        {lastReminder ? ` · ${i18n('smsLastReminder', { date: lastReminder })}` : ''}
      </Text>
      {errorLabel ? (
        <Text c="red" size="xs">
          {errorLabel}
        </Text>
      ) : null}
    </Stack>
  );
};

interface AssignmentMapSidebarProps {
  capabilities: AssignmentMapCapabilities;
  clearAssignment: boolean;
  filters: AssignmentMapFilters;
  filterOptions: AssignmentFilterOptions;
  filteredCount: number;
  totalCount: number;
  onClearAssignmentChange: (value: boolean) => void;
  onFiltersChange: (filters: AssignmentMapFilters) => void;
  i18n: (key: string, options?: Record<string, unknown>) => string;
  isNgp: boolean;
  mode: DrawActionMode;
  defaultMode: DrawActionMode;
  onModeChange: (mode: DrawActionMode) => void;
  period: SamplingPeriod;
  onPeriodChange: (period: SamplingPeriod) => void;
  periodWindowHint?: string;
  drawHint: string;
  preview: PreviewPolygonResponse | null;
  samplerOptions: { value: string; label: string }[];
  laboratoryOptions: { value: string; label: string }[];
  samplerUserId: string | null;
  onSamplerUserIdChange: (value: string | null) => void;
  onSamplerOrganizationIdChange: (value: string | null) => void;
  samplerOrganizationId: string | null;
  samplerOrganizationOptions: { value: string; label: string }[];
  reassignSamplerId: string | null;
  onReassignSamplerIdChange: (value: string | null) => void;
  visibleToSampler: boolean;
  onVisibleToSamplerChange: (value: boolean) => void;
  laboratoryId: string | null;
  onLaboratoryIdChange: (value: string | null) => void;
  primaryActionLabel: string;
  primaryActionDisabled: boolean;
  primaryActionLoading: boolean;
  onPrimaryAction: () => void;
  recentActions: RecentMapAction[];
  selectedSmsDispatchId: number | null;
  onSelectSmsDispatch: (id: number | null) => void;
  smsProgress?: SmsBatchProgress;
}

export const AssignmentMapSidebar: FC<AssignmentMapSidebarProps> = ({
  capabilities,
  clearAssignment,
  filters,
  filterOptions,
  filteredCount,
  totalCount,
  onClearAssignmentChange,
  onFiltersChange,
  i18n,
  isNgp,
  mode,
  defaultMode,
  onModeChange,
  period,
  onPeriodChange,
  periodWindowHint,
  drawHint,
  preview,
  samplerOptions,
  laboratoryOptions,
  samplerUserId,
  onSamplerUserIdChange,
  onSamplerOrganizationIdChange,
  samplerOrganizationId,
  samplerOrganizationOptions,
  reassignSamplerId,
  onReassignSamplerIdChange,
  visibleToSampler,
  onVisibleToSamplerChange,
  laboratoryId,
  onLaboratoryIdChange,
  primaryActionLabel,
  primaryActionDisabled,
  primaryActionLoading,
  onPrimaryAction,
  recentActions,
  selectedSmsDispatchId,
  onSelectSmsDispatch,
  smsProgress,
}) => (
  <Paper
    h="100%"
    mih={0}
    p={0}
    radius="sm"
    style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
    withBorder
  >
    <ScrollArea flex={1} mih={0} p="md" type="auto">
      <Stack gap="md" mih="100%">
        <Stack gap="sm">
          <Text fw={600} size="sm">
            {i18n('filtersTitle')}
          </Text>
          <AssignmentMapFilterBar
            filterOptions={filterOptions}
            filteredCount={filteredCount}
            filterVisibility={{
              organizations: capabilities.filterOrganizations,
              osOrganizations: capabilities.filterOsOrganizations,
              samplers: capabilities.filterSamplers,
              laboratories: capabilities.filterLaboratories,
              organic: capabilities.filterOrganic,
              assignmentState: capabilities.filterAssignmentState,
              analysisTypes: capabilities.filterAnalysisTypes,
            }}
            filters={filters}
            i18n={i18n}
            onChange={onFiltersChange}
            totalCount={totalCount}
          />
        </Stack>

        <Divider />

        <AssignmentMapActionPanel
          capabilities={capabilities}
          clearAssignment={clearAssignment}
          defaultMode={defaultMode}
          drawHint={drawHint}
          i18n={i18n}
          isNgp={isNgp}
          laboratoryId={laboratoryId}
          laboratoryOptions={laboratoryOptions}
          mode={mode}
          onClearAssignmentChange={onClearAssignmentChange}
          onLaboratoryIdChange={onLaboratoryIdChange}
          onModeChange={onModeChange}
          onPeriodChange={onPeriodChange}
          onPrimaryAction={onPrimaryAction}
          onReassignSamplerIdChange={onReassignSamplerIdChange}
          onSamplerOrganizationIdChange={onSamplerOrganizationIdChange}
          onSamplerUserIdChange={onSamplerUserIdChange}
          onVisibleToSamplerChange={onVisibleToSamplerChange}
          period={period}
          periodWindowHint={periodWindowHint}
          preview={preview}
          primaryActionDisabled={primaryActionDisabled}
          primaryActionLabel={primaryActionLabel}
          primaryActionLoading={primaryActionLoading}
          reassignSamplerId={reassignSamplerId}
          samplerOrganizationId={samplerOrganizationId}
          samplerOrganizationOptions={samplerOrganizationOptions}
          samplerOptions={samplerOptions}
          samplerUserId={samplerUserId}
          visibleToSampler={visibleToSampler}
        />

        {capabilities.smsReadOnly && selectedSmsDispatchId && smsProgress ? (
          <>
            <Divider />
            <Stack gap="xs">
              <Text fw={600} size="sm">
                {i18n('smsReadOnlyTitle')}
              </Text>
              <Text size="xs">
                {i18n('smsProgressSummary', {
                  responded: smsProgress.responded_count,
                  total: smsProgress.contracts_total,
                })}
              </Text>
              {smsProgress.farmers.map((farmer) => (
                <FarmerSmsRow key={farmer.farm_id} farmer={farmer} i18n={i18n} />
              ))}
            </Stack>
          </>
        ) : null}

        {capabilities.showRecentActions ? (
          <>
        <Divider />

        <Accordion defaultValue={null} variant="contained">
          <Accordion.Item value="recent-actions">
            <Accordion.Control>
              <Group gap="xs" justify="space-between" pr="xs" wrap="nowrap">
                <Text size="sm">{i18n('recentActions')}</Text>
                {recentActions.length ? (
                  <Badge size="sm" variant="light">
                    {recentActions.length}
                  </Badge>
                ) : null}
              </Group>
            </Accordion.Control>
            <Accordion.Panel>
              <Stack gap="xs">
                {recentActions.length ? (
                  recentActions.map((action) => {
                    const dispatchId = smsDispatchIdFromAction(action);
                    const selected =
                      dispatchId != null && dispatchId === selectedSmsDispatchId;

                    return (
                      <Paper
                        key={action.id}
                        onClick={() => {
                          if (dispatchId) onSelectSmsDispatch(dispatchId);
                        }}
                        p="xs"
                        radius="sm"
                        style={{
                          cursor: dispatchId ? 'pointer' : 'default',
                          borderColor: selected ? 'var(--mantine-color-blue-5)' : undefined,
                        }}
                        withBorder
                      >
                        <Stack gap={0}>
                          <Text fw={500} lineClamp={1} size="xs">
                            {i18n(actionTypeLabelKey(action.action_type as MapActionType))}
                          </Text>
                          <Text c="dimmed" size="xs">
                            {actionHistoryDate(action)} · {action.point_count} pt
                          </Text>
                        </Stack>
                      </Paper>
                    );
                  })
                ) : (
                  <Text c="dimmed" size="sm">
                    {i18n('noRecentActions')}
                  </Text>
                )}

                {selectedSmsDispatchId && smsProgress ? (
                  <Stack gap="xs" mt="xs">
                    <Text fw={600} size="xs">
                      {i18n('smsProgressSummary', {
                        responded: smsProgress.responded_count,
                        total: smsProgress.contracts_total,
                      })}
                    </Text>
                    {smsProgress.farmers.map((farmer) => (
                      <FarmerSmsRow key={farmer.farm_id} farmer={farmer} i18n={i18n} />
                    ))}
                  </Stack>
                ) : null}
              </Stack>
            </Accordion.Panel>
          </Accordion.Item>
        </Accordion>
          </>
        ) : null}
      </Stack>
    </ScrollArea>
  </Paper>
);
